import { useMemo } from "react";

import { useField, type FieldConfig } from "formik";
import Select, { createFilter, type Props as SelectProps } from "react-select";

import { apiSlice } from "app/apiSlice";
import { type Campaign } from "app/types";
import getCustomSelectStyles from "components/Forms/styles/getCustomSelectStyles";

const stableObject = {};

interface CampaignOption {
  value: string;
  label: string;
  name: string;
  shortId?: string;
  isAlreadyAssigned?: boolean;
}

interface CampaignSelectProps
  extends Omit<
      SelectProps<CampaignOption, false>,
      "options" | "value" | "onBlur"
    >,
    Pick<FieldConfig, "validate"> {
  name: string;
  creator_id?: string;
}

export const CampaignSelect = ({
  name,
  creator_id,
  onChange,
  validate,
  ...selectProps
}: CampaignSelectProps) => {
  const [field, meta, helpers] = useField({ name, validate });

  const { campaignOptions, isLoading: isLoadingCampaigns } =
    apiSlice.useGetCampaignsQuery(stableObject, {
      selectFromResult: ({ data, isLoading }) => ({
        campaignOptions:
          data?.results?.map((campaign: Campaign) => ({
            value: campaign._id,
            label: campaign.name,
            name: campaign.name,
            shortId: campaign.shortId,
          })) || [],
        isLoading,
      }),
    });

  // Fetch assignments when creator_id is provided
  const { assignedCampaignIds, isLoading: isLoadingAssignments } =
    apiSlice.useGetAssignmentsQuery(
      { user_id: creator_id, _source: "campaign select" },
      {
        selectFromResult: ({ data, isLoading }) => ({
          assignedCampaignIds:
            data?.results?.map(
              (assignment: any) =>
                assignment?.campaign_id?._id ?? assignment?.campaign_id,
            ) || [],
          isLoading,
        }),
        skip: !creator_id,
      },
    );

  const campaignOptionsWithAssignments = useMemo(() => {
    if (!campaignOptions.length) return [];

    return campaignOptions.map((campaign) => ({
      ...campaign,
      isAlreadyAssigned: creator_id
        ? assignedCampaignIds.includes(campaign.value)
        : false,
    }));
  }, [campaignOptions, assignedCampaignIds, creator_id]);

  const isLoading = isLoadingCampaigns || isLoadingAssignments;

  const selectedOption = useMemo(() => {
    return (
      campaignOptionsWithAssignments.find(
        (option) => option.value === field.value,
      ) || null
    );
  }, [campaignOptionsWithAssignments, field.value]);

  const handleChange = (
    selectedOption: CampaignOption | null,
    actionMeta: any,
  ) => {
    const newValue = selectedOption?.value || "";
    helpers.setValue(newValue);
    helpers.setTouched(true);

    // Call the optional onChange callback
    if (onChange) {
      onChange(selectedOption, actionMeta);
    }
  };

  // Custom filter using react-select's createFilter with proper typing
  const customFilter = useMemo(() => {
    return createFilter({
      ignoreCase: true,
      ignoreAccents: true,
      stringify: (option: any) => {
        // Include both name and shortId in search
        const data = option.data || option;
        const searchableText = [data.name, data.shortId || "", data._id]
          .filter(Boolean)
          .join(" ");

        return searchableText;
      },
    });
  }, []);

  const formatOptionLabel = (option: CampaignOption) => (
    <div>
      <div className="fw-medium d-flex align-items-center">
        {option.name}
        {option.isAlreadyAssigned && (
          <span className="badge bg-warning text-dark ms-2 small">
            Already Assigned
          </span>
        )}
      </div>
      {option.shortId && (
        <div className="text-muted small">{option.shortId}</div>
      )}
    </div>
  );

  const customStyles = getCustomSelectStyles("lg");

  const hasError = meta.touched && !!meta.error;

  return (
    <Select
      {...selectProps}
      id={name}
      name={name}
      value={selectedOption}
      onChange={handleChange}
      onBlur={() => helpers.setTouched(true)}
      options={campaignOptionsWithAssignments}
      placeholder={selectProps.placeholder || "Search campaigns..."}
      isSearchable={true}
      isLoading={isLoading}
      filterOption={customFilter}
      formatOptionLabel={formatOptionLabel}
      blurInputOnSelect={true}
      noOptionsMessage={({ inputValue }) =>
        inputValue
          ? `No campaigns found matching "${inputValue}"`
          : "No campaigns available"
      }
      isClearable={!selectProps.required}
      className={`custom-select ${hasError ? "is-invalid" : ""}`}
      classNames={{
        control: () => {
          let classes = "form-control";
          if (hasError) classes += " is-invalid";
          return classes;
        },
      }}
      styles={{
        control: (baseStyles, state) => ({
          ...customStyles.control(baseStyles, state),
          borderColor: hasError
            ? "var(--bs-form-invalid-border-color)"
            : state.isFocused
              ? "var(--custom-select-border-color-focus)"
              : "var(--custom-select-border-color)",
          boxShadow: hasError
            ? "var(--bs-form-invalid-focus-box-shadow)"
            : state.isFocused
              ? "var(--custom-select-focus-ring-shadow)"
              : "none",
        }),
        indicatorsContainer: customStyles.indicatorsContainer,
      }}
      aria-invalid={hasError ? "true" : "false"}
      aria-describedby={hasError ? `${name}-error` : undefined}
    />
  );
};
